import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { useIsAdminArea } from "../utils/portalLink";

/*
 * Shown wherever a target is drawn for a face whose definition is not stored.
 *
 * The results themselves are complete — every shot, score and centre count came
 * from the shot log and is correct. What is missing is the ring geometry, which
 * comes from the target definition file, so the target is drawn without scoring
 * rings. Saying that plainly matters: a shooter looking at a target with no
 * rings should know the picture is incomplete, not that their score is wrong.
 *
 * Two situations, which need different words. Nothing imported at all is a
 * setup step nobody has done yet. A few names missing from an otherwise stocked
 * list is a gap — usually a target this range uses that the file did not carry,
 * or a name spelt differently in the log. Counting the missing names in the
 * first case reads as "there are only four targets in existence", which is why
 * the empty case says so outright instead.
 */
export default function MissingTargetNotice({
  faces,
  /** How many definitions are stored in total; 0 means none have been imported. */
  storedCount,
  className = "",
}: {
  faces: string[];
  storedCount?: number;
  className?: string;
}) {
  const isAdmin = useIsAdminArea();
  if (!faces.length) return null;
  const nothingImported = storedCount === 0;
  const one = faces.length === 1;

  return (
    <div className={`flex items-start gap-2.5 rounded-xl border border-warning/40 bg-warning-soft px-4 py-3 ${className}`}>
      <AlertCircle size={16} className="mt-0.5 shrink-0 text-warning" />
      <div className="min-w-0 text-[13px] text-slate-700">
        <p className="font-bold text-warning">
          {nothingImported
            ? "No target definitions have been imported yet"
            : one
              ? "This target is not in the target list"
              : "Some targets are not in the target list"}
        </p>
        <p className="mt-0.5">
          {nothingImported ? (
            <>
              Target definitions hold the ring sizes every shot map is drawn from, and none are stored yet. Until they are, shot
              maps are drawn without scoring rings, so a target may not look correct.
            </>
          ) : (
            <>
              {one ? "This target is" : "These targets are"} not in the target list, so {one ? "its" : "their"} shot maps are
              drawn without scoring rings and may not look correct.
            </>
          )}{" "}
          The shots, scores and centre counts are complete and unaffected.
        </p>
        {isAdmin && (
          <p className="mt-1">
            <Link to="/admin/targets" className="font-bold text-primary hover:underline">
              Import the target definition file
            </Link>{" "}
            and {one ? "it is" : "these are"} corrected automatically — there is no need to upload the shoot again.
          </p>
        )}
      </div>
    </div>
  );
}
